import {
  studentProfilesApi,
  type CreateNotePayload,
  type UpdateNotePayload,
  type UpdateProfileIdentityPayload,
} from "@api/studentProfiles"
import { homeKeys } from "@features/home"
import { studentKeys } from "@features/students"
import { analytics } from "@lib/analytics"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { studentProfileKeys } from "./queryKeys"

export const useStudentProfile = (profileId: string | undefined) =>
  useQuery({
    queryKey: studentProfileKeys.overview(profileId ?? ""),
    queryFn: () => studentProfilesApi.overview(profileId as string),
    enabled: !!profileId,
    // Keeps the page rendered while a note mutation refetches, instead of
    // flashing the loader (frontend-performance.md §C-9).
    placeholderData: (prev) => prev,
  })

/** Invalidate the one profile a note mutation touched — not the whole tree. */
const useInvalidateProfile = (profileId: string) => {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: studentProfileKeys.overview(profileId) })
    void qc.invalidateQueries({ queryKey: studentProfileKeys.notes(profileId) })
  }
}

export const useCreateNote = (profileId: string) => {
  const invalidate = useInvalidateProfile(profileId)
  return useMutation({
    mutationFn: (data: CreateNotePayload) => studentProfilesApi.createNote(profileId, data),
    // PRD-57 — the FACT that a note was written, nothing else. The note body
    // is free text about one specific student and never leaves the product.
    onSuccess: () => {
      analytics.track("student_note_saved", { action: "created" })
      invalidate()
    },
  })
}

export const useUpdateNote = (profileId: string) => {
  const invalidate = useInvalidateProfile(profileId)
  return useMutation({
    mutationFn: ({ noteId, data }: { noteId: string; data: UpdateNotePayload }) =>
      studentProfilesApi.updateNote(noteId, data),
    onSuccess: () => {
      analytics.track("student_note_saved", { action: "updated" })
      invalidate()
    },
  })
}

export const useDeleteNote = (profileId: string) => {
  const invalidate = useInvalidateProfile(profileId)
  return useMutation({
    mutationFn: (noteId: string) => studentProfilesApi.deleteNote(noteId),
    onSuccess: () => {
      analytics.track("student_note_saved", { action: "deleted" })
      invalidate()
    },
  })
}

/**
 * PRD-40 §FR-19 — identity edit from the person page.
 *
 * Invalidates beyond this profile on purpose: the same name and address are
 * mirrored on every enrollment row, so every roster page, every per-student
 * envelope and the Home at-risk list are stale the instant this succeeds.
 * `studentKeys.all` is the students feature's public root for exactly that.
 */
export const useUpdateProfileIdentity = (profileId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateProfileIdentityPayload) =>
      studentProfilesApi.updateIdentity(profileId, data),
    onSuccess: () => {
      // Same business fact as the roster modal's save → one event, one
      // discriminating property (analytics-events.md §4).
      analytics.track("student_updated", { source: "profile" })
      void qc.invalidateQueries({ queryKey: studentProfileKeys.overview(profileId) })
      void qc.invalidateQueries({ queryKey: studentKeys.all })
      void qc.invalidateQueries({ queryKey: homeKeys.all })
    },
  })
}

export type { UpdateProfileIdentityPayload } from "@api/studentProfiles"
